import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { authAPI } from '@/lib/api/auth';
import { AuthLayout } from '@/layouts/AuthLayout';
import { VStack } from '@/components/core';

export default function ResetPassword() {
  const { t } = useTranslation(['auth', 'common']);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const token = searchParams.get('token') || '';

  useEffect(() => {
    if (!token) {
      setError(t('passwordReset.invalidLink', 'Ungültiger Link.'));
    }
  }, [token, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    if (password.length < 6) {
      setError(t('validation.passwordMinLength', 'Passwort zu kurz'));
      return;
    }
    if (password !== confirmPassword) {
      setError(t('validation.passwordMismatch', 'Passwörter stimmen nicht überein'));
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      await authAPI.resetPassword(token, password);
      setSuccess(t('passwordReset.success', 'Passwort wurde geändert.'));
      setTimeout(() => navigate('/auth?tab=login'), 1500);
    } catch {
      setError(t('passwordReset.failed', 'Zurücksetzen fehlgeschlagen.'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout>
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{t('passwordReset.setNew', 'Neues Passwort setzen')}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <VStack gap="md">
              <Input
                type="password"
                placeholder={t('register.passwordPlaceholder')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <Input
                type="password"
                placeholder={t('register.confirmPasswordPlaceholder')}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
              {error && <div className="text-sm text-error">{error}</div>}
              {success && <div className="text-sm text-success">{success}</div>}
              <Button type="submit" disabled={isLoading || !token} className="w-full">
                {isLoading ? t('common:loading') : t('common:buttons.save')}
              </Button>
            </VStack>
          </form>
        </CardContent>
      </Card>
    </AuthLayout>
  );
}
